import axios from "axios";
import { clearOrganizationCache } from "./services/backend";
import { config } from "./runtimeConfig";

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

// Store the latest access token for non-XHR consumers such as EventSource and
// audio URLs. Browser API requests use accessTokenProvider below so they can
// refresh the Keycloak token before sending a request.
let currentToken = null;
let accessTokenProvider = null;

export const setToken = (token) => {
  // Clear organization cache when token changes
  if (currentToken !== token) {
    clearOrganizationCache();
  }
  currentToken = token;
};

export const clearToken = () => {
  currentToken = null;
  clearOrganizationCache();
};

/**
 * Register the browser authentication adapter used by API requests.
 *
 * The provider returns an access token and may refresh it first. The Keycloak
 * refresh token remains owned by keycloak-js in memory and is never copied
 * into this module or persisted by the application.
 */
export const setAccessTokenProvider = (provider) => {
  accessTokenProvider = typeof provider === "function" ? provider : null;
};

export const Api = axios.create({
  baseURL: `${config.BACKEND_URL}${config.API}`,
  headers: {
    "Content-Type": "application/json",
  },
});

// Embed-mode (PRD §7.3): when running inside the iOS WebView, iOS keeps
// `window.__ETHOSPHERE_TOKEN__` up to date and dispatches
// `ethosphere:token-refreshed` on rotation. We re-read the global on every
// request as a defense-in-depth against stale `currentToken` (e.g. if a
// rotation fires mid-request and the React listener hasn't run yet).
const readEmbedToken = () => {
  if (typeof window === "undefined") return null;
  const raw = window.__ETHOSPHERE_TOKEN__;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
};

/**
 * Read the current bearer token (same precedence as the axios interceptor:
 * embed-injected first, then the Keycloak-seeded `currentToken`). Used by
 * non-XHR call sites that need to embed the token elsewhere — e.g.
 * `<audio>` URLs (`?access_token=…`) or EventSource URLs, both of which
 * cannot attach an Authorization header.
 */
export const getCurrentToken = () => readEmbedToken() || currentToken;

const getBrowserAccessToken = async (forceRefresh = false) => {
  if (!accessTokenProvider) {
    return currentToken;
  }

  const token = await accessTokenProvider({ forceRefresh });
  if (token) {
    setToken(token);
    return token;
  }

  return null;
};

Api.interceptors.request.use(
  async (config) => {
    // Include the CSRF token in the headers
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }

    // Prefer the embed-injected token (re-read fresh from window) so iOS
    // refreshes are picked up immediately. Fall back to the Keycloak-seeded
    // token for the regular browser flow.
    const embedToken = readEmbedToken();
    const token = embedToken || (await getBrowserAccessToken(false));
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and cache clearing
Api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;

    // Clear organization cache on authentication errors
    if (status === 401 || status === 403) {
      clearOrganizationCache();
    }

    // A tab resuming from the background can race Keycloak's expiration timer.
    // Force one refresh and retry the request. Do not do this for embed mode
    // (iOS owns token rotation), for authorization failures (403), or more than
    // once for the same request.
    const requestConfig = error.config;
    if (
      status === 401 &&
      requestConfig &&
      !requestConfig.__keycloakRefreshRetried &&
      !readEmbedToken() &&
      accessTokenProvider
    ) {
      requestConfig.__keycloakRefreshRetried = true;

      try {
        const token = await getBrowserAccessToken(true);
        if (token) {
          requestConfig.headers = requestConfig.headers || {};
          requestConfig.headers["Authorization"] = `Bearer ${token}`;
          return Api.request(requestConfig);
        }
      } catch {
        // Preserve the original backend error. Callers already handle rejected
        // API requests; refresh-token material and errors must not be surfaced.
      }
    }

    return Promise.reject(error);
  }
);
